import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Shield, Gavel } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge, LeagueChip } from "@/components/badges";
import EventCard from "@/components/EventCard";

export default function TeamDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${id}`);
      setData(data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (notFound) return (
    <div className="max-w-[900px] mx-auto p-6 text-center">
      <p className="text-slate-400 mt-16">Team not found.</p>
      <Link to="/teams" className="text-[#00E5A3] hover:underline text-sm">← Back to Teams</Link>
    </div>
  );
  if (!data) return <div className="p-6 text-center text-slate-500 font-mono">Loading…</div>;

  const { team, roster, rulings } = data;
  const out = roster.filter((p) => p.status === "out").length;
  const watch = roster.filter((p) => ["questionable", "doubtful"].includes(p.status)).length;

  return (
    <div className="max-w-[1200px] mx-auto p-4 sm:p-6">
      <Link to="/teams" data-testid="back-to-teams" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#00E5A3] mb-4">
        <ArrowLeft size={15} /> All teams
      </Link>

      <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-2"><LeagueChip league={team.league} /><span className="font-mono text-xs text-slate-500">{team.abbr}</span></div>
        <div className="flex items-center gap-3">
          <Shield size={26} className="text-[#00E5A3]" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">{team.name}</h1>
        </div>
        <div className="flex items-center gap-4 mt-4 text-sm font-mono text-slate-400">
          <span>{roster.length} players tracked</span>
          {watch > 0 && <span className="text-[#FFB020]">{watch} questionable</span>}
          {out > 0 && <span className="text-[#FF4D4D]">{out} out</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <h2 className="font-heading text-lg font-semibold text-slate-100 mb-3">Roster</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="team-roster">
            {roster.map((p) => (
              <Link key={p.id} to={`/players/${p.id}`} data-testid={`roster-player-${p.id}`}
                className="bg-[#0F1420] border border-[#232D42] rounded-lg p-3 hover:border-[#00E5A3]/40 transition-all">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-heading font-semibold text-slate-100 text-sm truncate">{p.name}</span>
                  <StatusBadge status={p.status} />
                </div>
                <div className="text-xs text-slate-500 font-mono">#{p.jersey} · {p.position}</div>
                {p.status_note && <p className="text-xs text-[#FFB020] mt-1 truncate">{p.status_note}</p>}
              </Link>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-3">
            <Gavel size={17} className="text-[#00E5A3]" />
            <h2 className="font-heading text-lg font-semibold text-slate-100">Recent official rulings</h2>
          </div>
          {rulings.length === 0 ? (
            <div className="bg-[#0F1420] border border-dashed border-[#232D42] rounded-xl p-6 text-center text-sm text-slate-500">
              No official rulings on record for this team yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="team-rulings">
              {rulings.map((r) => <EventCard key={r.id} event={r} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
